<!-- https://github.com/huntabyte/shadcn-svelte/blob/main/docs/src/lib/registry/ui/input/input.svelte -->

<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { HTMLInputAttributes, HTMLInputTypeAttribute } from 'svelte/elements';
  import { cx } from '@emotion/css';
  import { type WithElementRef } from '$lib/utils/components';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { ResponsiveVars } from '@twist/core';
  import CloseX from '$components/Icons/CloseX.svelte';

  type InputType = Exclude<HTMLInputTypeAttribute, 'file'>;
  type Props = WithElementRef<
    Omit<HTMLInputAttributes, 'type'> &
      ({ type: 'file'; files?: FileList } | { type?: InputType; files?: undefined })
  > & {
    icon?: Snippet;
    showClear?: boolean;
  };
  let {
    ref = $bindable(null),
    value = $bindable(),
    type,
    files = $bindable(),
    class: className,
    icon,
    showClear = false,
    ...restProps
  }: Props = $props();

  const responsiveVars = new ResponsiveVars()
    .addFluid('height', toFluidVarInput(45, 48))
    .addFluid('px', toFluidVarInput(24, 24))
    .addFluid('py', toFluidVarInput(9, 9))
    .addFluid('pr', toFluidVarInput(60, 60))
    .addFluid('pl', toFluidVarInput(57, 57))
    .addFluid('icon-size', toFluidVarInput(24, 24))
    .addFluid('gap', toFluidVarInput(9, 9))
    .addFluid('clear-button-size', toFluidVarInput(16, 16))
    .toCSS();
</script>

<div class={cx('input-wrapper', responsiveVars)}>
  {#if icon}
    <div class="icon">
      {@render icon()}
    </div>
  {/if}
  {#if type === 'file'}
    <input
      {...restProps}
      bind:this={ref}
      data-slot="input"
      class={className}
      type="file"
      bind:files
      bind:value
    />
  {:else}
    <input {...restProps} bind:this={ref} data-slot="input" class={className} {type} bind:value />
  {/if}
  {#if showClear && value}
    <button class="clear-button" onclick={() => (value = '')}>
      <CloseX />
    </button>
  {/if}
</div>

<style lang="scss">
  @import 'src/scss/variables';
  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    height: calc(var(--height) + 2px);
    margin: 2px; // prevent layout shift on hover or focus
  }

  input {
    width: 100%;
    height: 100%;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    font-family: var(--font-family-primary);
    color: $text-interactive-default-color;
    padding-block: var(--py);
    padding-right: calc(var(--px) + var(--clear-button-size));
    padding-left: calc(var(--px) + var(--icon-size) + var(--gap));
    border: 1px solid $border-interactive-default-color;
    border-radius: 1000px;
    outline: none;

    &::placeholder {
      color: $text-placeholder-color;
    }

    &::selection {
      background: var(--twist-color-gray-100);
      color: var(--twist-color-gray-0);
    }
    /* Remove browser's default search input styling */
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }

    &:disabled {
      cursor: not-allowed;
      color: $text-interactive-default-disabled-color;
      background: $background-depth-2-disabled-color;
      border: 1px solid $border-interactive-default-disabled-color;
    }

    &:hover {
      border: 1.5px solid $border-interactive-default-hover-color;
      margin: -0.5px; // prevent layout shift
    }

    &:focus {
      border: 3px solid $border-interactive-default-color;
      margin: -2px; // prevent layout shift
    }
  }

  .icon {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-interactive-default-color;
    width: var(--icon-size);
    height: var(--icon-size);
    left: var(--px);
  }

  .clear-button {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 10px;
    right: var(--px);
    cursor: pointer;
    color: $text-interactive-default-color;
    transition: color 0.2s ease;

    &:hover {
      color: $text-default-color;
    }

    :global(svg) {
      width: var(--clear-button-size);
      height: var(--clear-button-size);
    }
  }
</style>
